import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  GetProviderResponse,
  ListProvidersResponse,
  PaginatedResponse,
  PaginationParams,
  ProviderOptions,
  ProviderResource,
  UpdateProviderOptions,
} from "@prequel/import-react";

import { importClient } from "./sdk/client";

import { type PageOptions, useAllPages, usePage } from "@/api/pagination";
import { useToast } from "@/providers/ToastProvider";

export type { ProviderOptions, ProviderResource, UpdateProviderOptions } from "@prequel/import-react";

const routes = {
  list: "/import/providers",
  get: (id: string) => `/import/providers/${id}`,
  create: "/import/providers",
  update: (id: string) => `/import/providers/${id}`,
  delete: (id: string) => `/import/providers/${id}`,
};

const keys = { all: ["import", "providers"] as const };

export type ProviderFilters = {
  datalake_id?: string;
  is_enabled?: string;
};

export const listProvidersPage = (
  params: PaginationParams,
  filters?: ProviderFilters,
): Promise<PaginatedResponse<ProviderResource[]>> =>
  importClient.getPaginated<ListProvidersResponse>(routes.list, params, filters).then((res) => ({
    data: res.data.providers,
    has_next: res.has_next,
    next_url: res.next_url,
  }));

export function useListProviders(options?: PageOptions, filters?: ProviderFilters) {
  const datalakeID = filters?.datalake_id;
  const isEnabled = filters?.is_enabled;

  const fetchPage = useCallback(
    (params: PaginationParams) => listProvidersPage(params, { datalake_id: datalakeID, is_enabled: isEnabled }),
    [datalakeID, isEnabled],
  );

  return usePage<ProviderResource>(
    [...keys.all, { datalake_id: datalakeID, is_enabled: isEnabled }],
    fetchPage,
    options,
  );
}

export function useAllProviders() {
  return useAllPages<ProviderResource>([...keys.all], listProvidersPage);
}

export function useProvider(id: string) {
  return useQuery({
    queryKey: [...keys.all, id],
    queryFn: () => importClient.get<GetProviderResponse>(routes.get(id)).then((data) => data.provider),
  });
}

export function useCreateProvider() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (provider: ProviderOptions) =>
      importClient.post<GetProviderResponse>(routes.create, { provider }).then((data) => data.provider),
    onSuccess: (provider) => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Provider created successfully!" });
      navigate(`/import/providers/${provider.id}`);
    },
    // no onError: QueryProvider's mutationCache raises the error toast globally
  });
}

export function useUpdateProvider() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ id, provider }: { id: string; provider: UpdateProviderOptions; redirect?: boolean }) =>
      importClient.patch<GetProviderResponse>(routes.update(id), { provider }).then((data) => data.provider),
    onSuccess: (provider, { redirect = true }) => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Provider updated successfully!" });
      if (redirect) {
        navigate(`/import/providers/${provider.id}`);
      }
    },
  });
}

export function useDeleteProvider() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => importClient.delete(routes.delete(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Provider deleted successfully!" });
      navigate("/import/providers");
    },
  });
}
